import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { appointmentsApi } from '@/services/appointmentsApi'
import { useVerticalUI, type VerticalUiProfile } from '@/lib/verticalUI'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { isConversationDetailPath } from '@/utils/conversationRoutes'
import { SidebarRailOverlay, type RailHover } from './SidebarRailOverlay'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export type NavSubItem = {
  name: string
  path: string
  icon: string
  description?: string
}

export type NavItem = {
  name: string
  path: string
  icon: string
  description?: string
  dot?: 'green'
  /** Plan citas (A3): badge dinámico de solicitudes PENDING. */
  appointmentBadge?: boolean
  subItems?: NavSubItem[]
}

const OPERATIONS_NAV: NavItem[] = [
  { name: 'Dashboard', path: '/app/dashboard', icon: 'fas fa-chart-line', description: 'Métricas y vista general' },
  { name: 'Canales', path: '/app/instances', icon: 'fas fa-plug', description: 'Gestión de instancias', dot: 'green' },
  { name: 'Chats', path: '/app/conversations', icon: 'fas fa-comment-dots', description: 'Atención multicanal' },
  { name: 'Respuestas rápidas', path: '/app/canned-responses', icon: 'fas fa-bolt', description: 'Plantillas para asesores' },
  { name: 'Contactos', path: '/app/contacts', icon: 'fas fa-users', description: 'CRM de contactos' },
]

export function buildSalesNav(profile: VerticalUiProfile, _pendingAppointments: number): NavItem[] {
  const items: NavItem[] = []

  // 1. Si el vertical prioriza citas (salud, inmobiliaria, belleza, turismo, servicios)
  if (profile.nav.showAppointments && !profile.nav.showOrders) {
    items.push({
      name: profile.nav.primaryNavLabel,
      path: '/app/appointments',
      icon: 'fas fa-calendar-check',
      description: 'Agenda y confirmación manual',
      appointmentBadge: true,
    })
  }

  // 2. Si el vertical incluye órdenes
  if (profile.nav.showOrders) {
    items.push({
      name: profile.nav.primaryNavLabel,
      path: '/app/orders',
      icon: 'fas fa-receipt',
      description: 'Seguimiento y cierre comercial',
    })
  }

  // 3. Citas adicionales para vertical con órdenes y citas (educación, genérico)
  if (profile.nav.showOrders && profile.nav.showAppointments) {
    items.push({
      name: 'Citas',
      path: '/app/appointments',
      icon: 'fas fa-calendar-check',
      description: 'Agenda y confirmación manual',
      appointmentBadge: true,
    })
  }

  // 4. Catálogo adaptado al vertical
  items.push({
    name: profile.terminology.catalogName,
    path: '/app/catalog',
    icon: 'fas fa-store',
    description: 'Gestión y vitrina comercial',
    subItems: [
      { name: 'Vitrina y Enlaces', path: '/app/catalog', icon: 'fas fa-store', description: 'Vista previa y compartir' },
      { name: profile.terminology.itemsPluralName, path: '/app/products', icon: 'fas fa-box', description: 'Inventario y precios' },
      { name: 'Categorías', path: '/app/categories', icon: 'fas fa-folder', description: 'Departamentos y filtros' },
      { name: 'Promociones', path: '/app/promotions', icon: 'fas fa-bullhorn', description: 'Campañas y descuentos' },
    ],
  })

  // 5. CA-1.5: Enlace secundario si el vertical es de citas para no perder acceso a órdenes históricas
  if (!profile.nav.showOrders) {
    items.push({
      name: 'Historial de órdenes',
      path: '/app/orders',
      icon: 'fas fa-history',
      description: 'Registro histórico de órdenes',
    })
  }

  return items
}

const CONFIG_NAV: NavItem[] = [
  {
    name: 'Agente IA/Bot',
    path: '/app/bot-settings',
    icon: 'fas fa-robot',
    description: 'Personalidad, horarios y reglas',
    subItems: [
      { name: 'General y Prompt', path: '/app/bot-settings', icon: 'fas fa-sliders-h', description: 'Personalidad, tono y modelo' },
      { name: 'Horarios de atención', path: '/app/bot-settings/advanced', icon: 'fas fa-clock', description: 'Disponibilidad y fuera de turno' },
      { name: 'Preguntas Frecuentes', path: '/app/bot-settings/faq', icon: 'fas fa-question-circle', description: 'Base de conocimiento y puente FAQ' },
      { name: 'Pagos', path: '/app/bot-settings/payments', icon: 'fas fa-wallet', description: 'Métodos de pago y datos bancarios' },
      { name: 'Escalamiento', path: '/app/bot-settings/escalation', icon: 'fas fa-headset', description: 'Derivación y atención en equipo' },
      { name: 'Reglas propias', path: '/app/bot-settings/rules', icon: 'fas fa-scroll', description: 'Opción avanzada: indicaciones de tu negocio' },
    ],
  },
]

const ACCOUNT_NAV: NavItem[] = [
  { name: 'Organización', path: '/app/organization', icon: 'fas fa-building' },
  { name: 'Mi Perfil', path: '/app/profile', icon: 'fas fa-user' },
  { name: 'Facturación', path: '/app/billing', icon: 'fas fa-credit-card' },
  { name: 'Etiquetas', path: '/app/tags', icon: 'fas fa-tag' },
]

const STORAGE_KEY = 'sidebar-collapsed'

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  /**
   * Persiste solo los cambios hechos por el usuario (`applyUserCollapsed`):
   * el auto-contraer al abrir una conversación es transitorio y no debe
   * arrastrarse a otras secciones ni a la siguiente sesión.
   */
  const applyUserCollapsed = (next: boolean) => {
    setIsCollapsed(next)
    try {
      localStorage.setItem(STORAGE_KEY, String(next))
    } catch {
      /* almacenamiento no disponible: se ignora, el estado vuelve al default */
    }
  }

  const isDesktop = useIsDesktop()
  /** En móvil el cajón hamburguesa siempre se abre expandido (nunca el rail). */
  const isRailCollapsed = isCollapsed && isDesktop

  useEffect(() => {
    const handleExpand = () => setIsCollapsed(false)
    window.addEventListener('iridia:expand-sidebar', handleExpand)
    return () => window.removeEventListener('iridia:expand-sidebar', handleExpand)
  }, [])

  // Auto-contraer el sidebar en escritorio al abrir el detalle de una
  // conversación; se restaura al salir de esa vista.
  const autoCollapsedRef = useRef(false)
  useEffect(() => {
    if (!isDesktop) return
    const inConversationDetail = isConversationDetailPath(location.pathname)
    if (inConversationDetail && !autoCollapsedRef.current) {
      autoCollapsedRef.current = true
      setIsCollapsed(true)
    } else if (!inConversationDetail && autoCollapsedRef.current) {
      autoCollapsedRef.current = false
      setIsCollapsed(false)
    }
  }, [location.pathname, isDesktop])

  // Tooltips/flyouts del rail colapsado: se anclan fuera del nav scrolleable
  // (el overflow-y-auto los recortaría). El contrato acepta también los
  // eventos de foco para accesibilidad por teclado.
  const [railHover, setRailHover] = useState<RailHover | null>(null)
  const railPanelRef = useRef<HTMLDivElement | null>(null)

  const openRailHover = (item: NavItem) => (event: { currentTarget: HTMLElement }) => {
    setRailHover({ item, rect: event.currentTarget.getBoundingClientRect() })
  }

  const closeRailHover = (event: { relatedTarget: EventTarget | null }) => {
    if (railPanelRef.current?.contains(event.relatedTarget as Node | null)) return
    setRailHover(null)
  }

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  // Plan citas (A3): badge dinámico de solicitudes PENDING en el enlace Citas.
  const [pendingAppointments, setPendingAppointments] = useState(0)

  const { profile } = useVerticalUI()
  const salesNav = useMemo(
    () => buildSalesNav(profile, pendingAppointments),
    [profile, pendingAppointments]
  )

  useEffect(() => {
    if (!user || !profile.nav.showAppointments) {
      setPendingAppointments(0)
      return
    }

    let cancelled = false
    const loadCount = () => {
      appointmentsApi
        .getPendingCount()
        .then((count) => {
          if (!cancelled) setPendingAppointments(count)
        })
        .catch(() => {})
    }
    loadCount()
    const interval = window.setInterval(loadCount, 60_000)
    window.addEventListener('focus', loadCount)
    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('focus', loadCount)
    }
  }, [profile.nav.showAppointments, user])

  // Don't unmount the sidebar if user profile is partially hydrated or loading
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`)

  const isGroupActive = (item: NavItem) =>
    Boolean(item.subItems?.some((sub) => location.pathname === sub.path || location.pathname.startsWith(`${sub.path}/`)))

  const isGroupExpanded = (item: NavItem) => {
    if (expandedGroups[item.name] !== undefined) {
      return expandedGroups[item.name]
    }
    return isGroupActive(item)
  }

  const toggleGroup = (name: string, defaultExpanded: boolean) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [name]: !(prev[name] ?? defaultExpanded),
    }))
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const displayName = user?.name || 'Admin'
  const displayEmail = user?.email || 'admin@iridiachat.com'
  const initials = displayName.charAt(0).toUpperCase()

  /**
   * Ancla de tour (Driver.js v1) para un enlace de navegación. Se resuelve por
   * path para que los pasos apunten al enlace concreto sin depender de índices
   * ni de la variante vertical del menú.
   */
  const navTourAnchor = (path: string): string | undefined => {
    switch (path) {
      case '/app/dashboard':
        return 'nav-dashboard'
      case '/app/catalog':
      case '/app/products':
        return 'nav-catalog'
      case '/app/conversations':
        return 'nav-conversations'
      case '/app/instances':
        return 'nav-channels'
      case '/app/orders':
        return 'nav-orders'
      case '/app/bot-settings':
        return 'nav-bot-settings'
      default:
        return undefined
    }
  }

  const renderCompactItem = (item: NavItem) => {
    const hasSub = Boolean(item.subItems && item.subItems.length > 0)
    const groupActive = hasSub ? isGroupActive(item) : false
    const active = isActive(item.path) || groupActive
    const tourAnchor = navTourAnchor(item.path)

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        data-tour={tourAnchor}
        aria-label={item.name}
        aria-haspopup={hasSub ? 'menu' : undefined}
        onMouseEnter={openRailHover(item)}
        onMouseLeave={closeRailHover}
        onFocus={openRailHover(item)}
        onBlur={closeRailHover}
        className={[
          'relative p-3 rounded-xl transition-colors w-full flex justify-center',
          active ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10',
        ].join(' ')}
      >
        <i className={`${item.icon} text-xl`} aria-hidden="true" />
        {hasSub && <span className="absolute bottom-1.5 right-2 h-1.5 w-1.5 rounded-full bg-emerald-300" />}
        {item.dot === 'green' && (
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full border-2 border-[#0F172A] bg-emerald-400" />
        )}
      </Link>
    )
  }

  const renderExpandedItem = (item: NavItem) => {
    const hasSub = Boolean(item.subItems && item.subItems.length > 0)
    const groupActive = hasSub ? isGroupActive(item) : false
    const active = isActive(item.path) || groupActive
    const expanded = hasSub ? isGroupExpanded(item) : false
    const tourAnchor = navTourAnchor(item.path)

    if (hasSub && item.subItems) {
      return (
        <div key={item.name} className="space-y-1">
          <div
            data-tour={tourAnchor}
            className={[
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-white transition-all cursor-pointer select-none',
              groupActive ? 'bg-white/10 text-white' : 'text-white/85 hover:bg-white/5 hover:text-white',
            ].join(' ')}
            onClick={() => toggleGroup(item.name, groupActive)}
          >
            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${groupActive ? 'bg-white/10' : 'bg-white/5'}`}>
              <i className={`${item.icon} text-base text-white`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">{item.name}</p>
              {item.description && <p className="truncate text-xs text-white/60">{item.description}</p>}
            </div>
            <button
              type="button"
              className="p-1 text-white/60 hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleGroup(item.name, groupActive)
              }}
              aria-label={expanded ? 'Colapsar submenú' : 'Expandir submenú'}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>

          {expanded && (
            <div className="ml-5 mt-1 space-y-1 border-l-2 border-white/15 pl-2.5">
              {item.subItems.map((sub) => {
                const isSubActive =
                  location.pathname === sub.path ||
                  (sub.path !== '/app/catalog' && sub.path !== '/app/bot-settings' && location.pathname.startsWith(`${sub.path}/`))
                return (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    onClick={onClose}
                    className={[
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs transition-colors',
                      isSubActive
                        ? 'bg-white/20 font-semibold text-white shadow-sm'
                        : 'text-white/70 hover:bg-white/10 hover:text-white',
                    ].join(' ')}
                  >
                    <i className={`${sub.icon} w-3.5 text-center text-xs`} />
                    <span className="truncate">{sub.name}</span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        data-tour={tourAnchor}
        className={[
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-white transition-all',
          active ? 'bg-white/10 text-white' : 'text-white/85 hover:bg-white/5 hover:text-white',
        ].join(' ')}
      >
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${active ? 'bg-white/10' : 'bg-white/5'}`}>
          <i className={`${item.icon} text-base text-white`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{item.name}</p>
          {item.description && <p className="truncate text-xs text-white/60">{item.description}</p>}
        </div>
        {item.dot === 'green' && <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />}
        {item.appointmentBadge && pendingAppointments > 0 && (
          <span
            data-testid="appointments-pending-badge"
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white"
          >
            {pendingAppointments > 99 ? '99+' : pendingAppointments}
          </span>
        )}
      </Link>
    )
  }

  if (isRailCollapsed) {
    return (
      <>
        {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onClose} />}
        <aside
          data-tour="sidebar"
          className={[
            'fixed left-0 top-0 z-30 flex h-full w-[80px] flex-col items-center bg-[#0F172A] py-6 shadow-xl',
            // En escritorio queda fijo bajo el header: el nav scrollea por
            // dentro y el botón de expandir siempre es visible.
            'transform transition-all duration-300 ease-in-out lg:sticky lg:top-16 lg:z-0 lg:translate-x-0 lg:h-[calc(100vh-4rem)]',
            isOpen ? 'translate-x-0' : '-translate-x-full',
          ].join(' ')}
        >
          <div className="mb-6 flex-shrink-0 flex flex-col items-center gap-3">
            <Link to="/app/dashboard" title="IridiaChat" aria-label="IridiaChat">
              <img
                src="/new/IridiaChat_Isotipo_Icono_Transparente.png"
                alt="IridiaChat"
                className="h-10 w-10 object-contain rounded-xl hover:scale-105 transition-transform"
              />
            </Link>
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-600/20 text-xs font-bold text-indigo-300" title={displayName}>
              {initials}
            </div>
          </div>

          <nav className="sidebar-scroll flex w-full flex-1 flex-col items-center gap-2 overflow-y-auto px-2">
            {OPERATIONS_NAV.map(renderCompactItem)}
            <div className="my-1 h-px w-8 bg-white/10" />
            {salesNav.map(renderCompactItem)}
            <div className="my-1 h-px w-8 bg-white/10" />
            {CONFIG_NAV.map(renderCompactItem)}
            <div className="my-1 h-px w-8 bg-white/10" />
            {ACCOUNT_NAV.map(renderCompactItem)}
          </nav>

          <div className="mt-auto flex w-full justify-center border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={() => applyUserCollapsed(false)}
              className="rounded-xl p-3 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Expandir menú"
              title="Expandir menú"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </aside>

        {railHover && (
          <SidebarRailOverlay
            hover={railHover}
            pathname={location.pathname}
            onNavigate={() => setRailHover(null)}
            onClose={() => setRailHover(null)}
            panelRef={railPanelRef}
          />
        )}
      </>
    )
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onClose} />}
      <aside
        data-tour="sidebar"
        className={[
          'fixed left-0 top-0 z-30 flex h-full w-[280px] flex-col bg-[#0F172A] text-white shadow-xl',
          // En escritorio queda fijo bajo el header: el nav scrollea por
          // dentro y el botón de contraer (arriba y abajo) siempre es visible.
          'transform transition-all duration-300 ease-in-out lg:sticky lg:top-16 lg:z-0 lg:translate-x-0 lg:h-[calc(100vh-4rem)]',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-white/10 px-5 py-4">
          <Link to="/app/dashboard" className="flex items-center">
            <img
              src="/new/IridiaChat_Logo_Fondo_Oscuro_Transparente.png"
              alt="IridiaChat"
              className="h-8 w-auto object-contain"
            />
            <span className="sr-only">IridiaChat</span>
          </Link>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => applyUserCollapsed(true)}
              className="hidden rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white lg:inline-flex"
              aria-label="Contraer menú"
              title="Contraer menú"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-white/10 lg:hidden" aria-label="Cerrar menú">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-shrink-0 bg-slate-800/60 p-4 border-b border-white/5">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-600/20 font-bold text-sm text-indigo-300">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-white/70">{displayName}</p>
              <p className="truncate text-sm font-semibold text-white">{displayEmail}</p>
            </div>
            <button type="button" onClick={handleLogout} className="rounded-lg p-2 transition-colors hover:bg-white/10" title="Cerrar sesión">
              <i className="fas fa-sign-out-alt text-lg" />
            </button>
          </div>
        </div>

        <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4">
          <Section title="Operación" items={OPERATIONS_NAV} renderItem={renderExpandedItem} />
          <Section title="Ventas" items={salesNav} renderItem={renderExpandedItem} />
          <Section title="Configuración" items={CONFIG_NAV} renderItem={renderExpandedItem} />
          <Section title="Cuenta" items={ACCOUNT_NAV} renderItem={renderExpandedItem} />
        </nav>

        <div className="flex-shrink-0 border-t border-white/10 p-4">
          <button
            type="button"
            onClick={() => applyUserCollapsed(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
            Contraer menú
          </button>
        </div>
      </aside>
    </>
  )
}

function Section({
  title,
  items,
  renderItem,
}: {
  title: string
  items: NavItem[]
  renderItem: (item: NavItem) => ReactNode
}) {
  return (
    <div className="mb-5">
      <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">{title}</div>
      <div className="space-y-1">{items.map(renderItem)}</div>
    </div>
  )
}



